import { getLocalText } from '@/pages/datalab/utils/utils';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import { convertCustomColumnToColumnRawInfo, type CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import type { IColumnRawInfo } from '@dbl/core/dataSource';
import type {
  IBatchAddLevel1Node,
  IBatchAddLevel2Node,
} from '../types';

/** 接口 column_class_list 中维度/指标分组的展示名，用于识别分组类型 */
const DIMENSION_GROUP_NAMES = ['维度'];
const METRIC_GROUP_NAMES = ['指标'];

/**
 * 单表下按「维度 / 指标」拆成二级节点，供下拉右侧字段分组标题展示。
 * 识别分组用中文 name；展示用 name / name_en 双语，与 menuTab 一致。
 */
const buildTableLevel2Nodes = (
  tableInfo: IGetTableInfoDto,
): IBatchAddLevel2Node[] => {
  const nodes: IBatchAddLevel2Node[] = [];
  (tableInfo.column_class_list || []).forEach((classItem, index) => {
    // 统计指标 / 分类维度 / 过滤条件三处批量添加均展示完整字段池（维度 + 指标）
    // 过滤非「维度」「指标」分组（如过滤条件分组不在批量下拉展示）
    if (
      !DIMENSION_GROUP_NAMES.includes(classItem.name) &&
      !METRIC_GROUP_NAMES.includes(classItem.name)
    ) {
      return;
    }
    const fields = (classItem.columns || []) as IColumnRawInfo[];
    if (fields.length === 0) {
      return;
    }
    const groupType = METRIC_GROUP_NAMES.includes(classItem.name)
      ? 'metricGroup'
      : 'dimensionGroup';
    nodes.push({
      id: `${tableInfo.table_id}_${groupType}_${index}`,
      type: groupType,
      label: classItem.name,
      labelEn: classItem.name_en,
      fields,
    });
  });
  return nodes;
};

/** 自定义字段统一归入一个二级节点，字段经 convertCustomColumnToColumnRawInfo 对齐物理表结构 */
const buildCustomLevel2Node = (
  customColumns: CustomColumn[],
): IBatchAddLevel2Node | null => {
  if (!customColumns?.length) {
    return null;
  }
  const fields = customColumns.map(item =>
    convertCustomColumnToColumnRawInfo(item),
  );
  return {
    id: 'custom_fields',
    type: 'customField',
    fields,
  };
};

/**
 * 构建批量添加字段树：自定义字段 + 各数据表（维度/指标分组）。
 * 三处落区（过滤/指标/维度）共用完整字段池，不按 targetKey 裁剪。
 */
export const buildFieldTree = (
  dataTables: IGetTableInfoDto[],
  customColumns: CustomColumn[],
  isEn: boolean,
  customFieldLabel: string,
): IBatchAddLevel1Node[] => {
  const tree: IBatchAddLevel1Node[] = [];
  const customNode = buildCustomLevel2Node(customColumns || []);
  if (customNode) {
    tree.push({
      id: 'custom',
      type: 'custom',
      label: customFieldLabel,
      children: [customNode],
    });
  }
  (dataTables || []).forEach(tableInfo => {
    const children = buildTableLevel2Nodes(tableInfo);
    if (children.length === 0) {
      return;
    }
    tree.push({
      id: tableInfo.table_id,
      type: 'table',
      label: getLocalText(
        isEn,
        tableInfo.customize_name,
        tableInfo.customize_name_en,
        tableInfo.table_name,
      ),
      tableInfo,
      children,
    });
  });
  return tree;
};

/** 将树结构拍平为字段列表，供 key 映射与 applyBatchFields 查找 rawField */
export const flattenTreeFields = (
  tree: IBatchAddLevel1Node[],
): IColumnRawInfo[] => {
  const result: IColumnRawInfo[] = [];
  tree.forEach(level1 => {
    level1.children.forEach(level2 => {
      result.push(...level2.fields);
    });
  });
  return result;
};
